import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {computed, inject, Injectable, PLATFORM_ID, signal} from '@angular/core';

import {BusinessCallingApiService} from '@shared/services/api/conversation/business-calling-api.service';
import {NotifyService} from '@shared/services/global/notify.service';
import {AuthService} from '@core/authentication/auth.service';
import {ConversationRealtimeService} from '@features/conversation/services/conversation-realtime.service';
import {CONVERSATION_TYPE, IConversation} from '@features/conversation/models/conversation.model';
import {CHANNEL} from '@features/conversation/models/channel.model';

export enum CALL_STATUS {
    CONNECTED = 1, // user gọi page
    RINGING = 2, // page gọi user
    ACCEPTED = 3, // page gọi user
    COMPLETED = 4, // khi chấm dứt cuộc gọi
    IN_CALLING = 5, // tự khai báo, đánh dấu cuộc gọi đang diễn ra
    REJECTED = 6, // user gọi page
    MEDIA_UPDATE = 7, // page gọi user, user bấm nghe máy sẽ có webhook này
    CANCELLED = 8, // tự khai báo
}

export enum CALLING_DIRECTION {
  INCOMING = 'in',
  OUTGOING = 'out',
}

export interface IBusinessCallingMediaPermission {
  microphoneState: PermissionState | null;
  audioInputLabel: string;
  audioOutputLabel: string;
  isAudioPlaybackUnlocked: boolean;
  isRequestingMicrophone: boolean;
  isUnlockingAudio: boolean;
}

export interface IBusinessCallingTimer {
  callTimer: ReturnType<typeof setInterval> | null;
  totalCallSeconds: number;
  displayCallTime: string;
}

export interface IBusinessCallingItem {
  id?: string | number;
  pageId?: string | number;
  pageUserId?: string | number;
  pageUserName?: string;
  customerAvatar?: string;
  phoneNumberId?: string | number;
  displayPhoneNumber?: string;
  handledById?: string | number;
  handledByName?: string;
  status?: CALL_STATUS | string;
  direction?: CALLING_DIRECTION | string;
  createdAt?: number;
  _label?: string;
  duration?: number;
  _isMissed?: boolean;
  isConnecting?: boolean;
  isEnding?: boolean;
  session?: {
    sdp_response?: { sdp?: string; sdp_type?: RTCSdpType };
    sdp_renegotiation?: { sdp?: string; sdp_type?: RTCSdpType };
    sdp?: string;
    sdp_type?: RTCSdpType;

    [key: string]: unknown;
  };

  [key: string]: unknown;
}

const CALL_STATUS_MAP: Record<string, CALL_STATUS> = {
  '1': CALL_STATUS.CONNECTED,
  connected: CALL_STATUS.CONNECTED,
  connect: CALL_STATUS.CONNECTED,
  '2': CALL_STATUS.RINGING,
  ringing: CALL_STATUS.RINGING,
  '3': CALL_STATUS.ACCEPTED,
  accepted: CALL_STATUS.ACCEPTED,
  '4': CALL_STATUS.COMPLETED,
  completed: CALL_STATUS.COMPLETED,
  terminate: CALL_STATUS.COMPLETED,
  '5': CALL_STATUS.IN_CALLING,
  in_calling: CALL_STATUS.IN_CALLING,
  '6': CALL_STATUS.REJECTED,
  rejected: CALL_STATUS.REJECTED,
  '7': CALL_STATUS.MEDIA_UPDATE,
  media_update: CALL_STATUS.MEDIA_UPDATE,
  '8': CALL_STATUS.CANCELLED,
  cancelled: CALL_STATUS.CANCELLED,
};

const CALLING_DIRECTION_MAP: Record<string, CALLING_DIRECTION> = {
  in: CALLING_DIRECTION.INCOMING,
  incoming: CALLING_DIRECTION.INCOMING,
  user_initiated: CALLING_DIRECTION.INCOMING,
  user_initiated_in: CALLING_DIRECTION.INCOMING,
  out: CALLING_DIRECTION.OUTGOING,
  outgoing: CALLING_DIRECTION.OUTGOING,
};

type LegacyGetUserMedia = (
  constraints: MediaStreamConstraints,
  successCallback: (stream: MediaStream) => void,
  errorCallback: (error: DOMException) => void,
) => void;

type NavigatorWithLegacyMedia = Navigator & {
  getUserMedia?: LegacyGetUserMedia;
  webkitGetUserMedia?: LegacyGetUserMedia;
  mozGetUserMedia?: LegacyGetUserMedia;
};

export interface IBusinessCallingState {
  activeTab: number;
  isCallingIn: boolean;
  isCallingOut: boolean;
  isCalling: boolean;
  isProcessing: boolean;
  handledByUsers: unknown[];
  callTimer: IBusinessCallingTimer;
  currentCalling: IBusinessCallingItem | null;
  incomingCallList: IBusinessCallingItem[];
  callingList: {
    items: IBusinessCallingItem[];
    next: unknown;
    isProcessing: boolean;
  };
}

@Injectable({providedIn: 'root'})
export class BusinessCallingService {
  private readonly _stateVersion = signal(0);
  readonly stateVersion = this._stateVersion.asReadonly();
  readonly incomingDisplayList = computed(() => {
    this._stateVersion();
    return this.getIncomingDisplayList();
  });

  mediaPermission: IBusinessCallingMediaPermission = {
    microphoneState: null,
    audioInputLabel: '',
    audioOutputLabel: '',
    isAudioPlaybackUnlocked: true,
    isRequestingMicrophone: false,
    isUnlockingAudio: false,
  };

  callingObj: IBusinessCallingState = {
    activeTab: 0,
    isCallingIn: false,
    isCallingOut: false,
    isCalling: false,
    isProcessing: false,
    handledByUsers: [],
    callTimer: {
      callTimer: null,
      totalCallSeconds: 0,
      displayCallTime: '00:00',
    },
    currentCalling: null,
    incomingCallList: [],
    callingList: {
      items: [],
      next: null,
      isProcessing: false,
    },
  };

  isMicEnabled = true;
  peerConnection: RTCPeerConnection | null = null;

  private remoteAudioEl?: HTMLAudioElement;
  private readonly businessCallingApiService = inject(BusinessCallingApiService);
  private readonly notify = inject(NotifyService);
  private readonly authService = inject(AuthService);
  private readonly realtime = inject(ConversationRealtimeService);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly handledRemoteSdpKeys = new Set<string>();
  private stateTouchQueued = false;

  constructor() {
    void this.refreshMediaPermissions();

    if (this.isBrowser && navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', () => {
        void this.refreshAudioDevices();
      });
    }
  }

  isMyActiveCalling(userId: string | number): boolean {
    return this.callingObj.isCalling
      && this.callingObj.currentCalling?.handledById == userId;
  }

  canAcceptCall(userId: string | number): boolean {
    return !this.isMyActiveCalling(userId) && !this.callingObj.isCallingOut;
  }

  isCurrentCalling(callId: string | number | null | undefined): boolean {
    return !!callId && this.callingObj.currentCalling?.id == callId;
  }

  hasActiveCurrentCalling(userId?: string | number): boolean {
    const currentCalling = this.callingObj.currentCalling;
    if (!currentCalling?.id) {
      return false;
    }

    return this.callingObj.isCallingOut
      || this.callingObj.isCalling
      || (!!userId
        && currentCalling.handledById == userId
        && currentCalling.status === CALL_STATUS.IN_CALLING);
  }

  loadIncomingList(pageIds: Array<string> = [...this.authService.getPageIds(CHANNEL.FACEBOOK), ...this.authService.getPageIds(CHANNEL.WHATSAPP)]): void {
    const normalizedPageIds = pageIds
      .map(pageId => String(pageId))
      .filter(pageId => !!pageId);

    if (!normalizedPageIds.length || this.callingObj.callingList.isProcessing) {
      return;
    }

    this.callingObj.callingList.isProcessing = true;
    this.businessCallingApiService.getIncomingList({pageId: normalizedPageIds}).subscribe({
      next: res => {
        const incomingCalls = this.extractIncomingCalls(res);
        this.callingObj.incomingCallList = [];

        incomingCalls.forEach(call => {
          this.normalizeCallingData(call);
          if (call.status !== CALL_STATUS.CONNECTED || call.direction !== CALLING_DIRECTION.INCOMING) {
            return;
          }

          if (!this.isCurrentCalling(call.id)) {
            this.addIncomingCall(call);
          }
        });
      },
      error: err => {
        console.log('Lỗi tải danh sách cuộc gọi đến', err?.message || err);
        this.callingObj.callingList.isProcessing = false;
        this.touchState();
      },
      complete: () => {
        this.callingObj.callingList.isProcessing = false;
        this.touchState();
      },
    });
  }

  changeCallingTab(
    tab: number,
    conversation: IConversation | null | undefined,
    pageUserId: string | number | null | undefined = null,
  ): void {
    if (this.callingObj.activeTab === tab && this.callingObj.callingList.items.length) {
      return;
    }

    this.callingObj.activeTab = tab;
    this.resetCallingList();
    this.loadCallingList(conversation, pageUserId);
  }

  resetCallingList(): void {
    this.callingObj.callingList = {
      items: [],
      next: null,
      isProcessing: false,
    };
    this.touchState();
  }

  loadCallingList(
    conversation: IConversation | null | undefined,
    pageUserId: string | number | null | undefined = null,
    next: unknown = null,
  ): void {
    if (!conversation?.fanpageId || this.callingObj.callingList.isProcessing) {
      return;
    }

    const params: { pageId: string; pageUserId?: string | null; after?: unknown } = {
      pageId: String(conversation.fanpageId),
      pageUserId: this.callingObj.activeTab === 0 && pageUserId ? String(pageUserId) : null,
    };

    if (next) {
      params.after = next;
    }

    this.callingObj.callingList.isProcessing = true;
    this.callingObj.callingList.next = next || null;
    this.touchState();
    this.businessCallingApiService.getCallingList(params).subscribe({
      next: res => {
        const data = (res as any)?.data ?? res;
        const rawSource = data?.result ?? data ?? [];
        const rawItems: IBusinessCallingItem[] = (Array.isArray(rawSource) ? rawSource : [])
          .filter((item: unknown): item is IBusinessCallingItem => !!item && typeof item === 'object');
        rawItems.forEach(call => this.normalizeCallingData(call));

        this.callingObj.callingList.items = rawItems;
        this.callingObj.callingList.next = Array.isArray((res as any)?.next) && (res as any).next.length
          ? (res as any).next
          : null;
      },
      error: err => {
        console.log('Lỗi tải lịch sử cuộc gọi', err?.message || err);
        if (!next) {
          this.callingObj.callingList.items = [];
          this.callingObj.callingList.next = null;
        }
        this.callingObj.callingList.isProcessing = false;
        this.touchState();
      },
      complete: () => {
        this.callingObj.callingList.isProcessing = false;
        this.touchState();
      },
    });
  }

  setIncomingCalling(call: IBusinessCallingItem): void {
    if (!call?.id) {
      return;
    }

    if (this.isCurrentCalling(call.id)) {
      this.callingObj.currentCalling = {
        ...(this.callingObj.currentCalling || {}),
        ...call,
      };
      this.touchState();
      return;
    }

    if (this.callingObj.currentCalling?.id) {
      this.addIncomingCall(call);
      return;
    }

    this.callingObj.isCallingIn = true;
    this.callingObj.currentCalling = call;
    this.touchState();
  }

  syncHandledCalling(call: IBusinessCallingItem, userId: string | number): void {
    if (!call?.id) {
      return;
    }

    this.removeIncomingCall(call.id);

    const current = this.isCurrentCalling(call.id);
    const isHandledByMe = !!call.handledById && call.handledById == userId;

    if (!current && this.hasActiveCurrentCalling(userId)) {
      return;
    }

    if (!current && this.callingObj.currentCalling?.id && !isHandledByMe) {
      return;
    }

    this.callingObj.currentCalling = {
      ...(current ? this.callingObj.currentCalling : {}),
      ...call,
    };
    this.callingObj.isCallingIn = false;
    this.callingObj.isCallingOut = false;
    this.callingObj.isCalling = isHandledByMe;
    this.touchState();
  }

  syncCallingFromConversationDetail(
    call: IBusinessCallingItem | null | undefined,
    conversation: IConversation | null | undefined,
  ): void {
    if (!conversation?.fanpageId || !conversation?.fanpageUserId) {
      return;
    }

    if (!call?.id) {
      const currentCalling = this.callingObj.currentCalling;
      if (currentCalling?.pageId == conversation.fanpageId
        && currentCalling?.pageUserId == conversation.fanpageUserId
        && !this.callingObj.isCalling
        && !this.callingObj.isCallingOut) {
        this.callingObj.currentCalling = null;
        this.callingObj.isCallingIn = false;
        this.touchState();
      }
      return;
    }

    const detailCalling: IBusinessCallingItem = {
      ...call,
      pageId: call.pageId || conversation.fanpageId,
      pageUserId: call.pageUserId || conversation.fanpageUserId,
      pageUserName: call.pageUserName || conversation.fanpageUserName,
      customerAvatar: call.customerAvatar || conversation.customerAvatar,
    };
    this.normalizeCallingData(detailCalling);
    if (!detailCalling.id) {
      return;
    }

    const isHandledByMe = !!detailCalling.handledById
      && detailCalling.handledById == this.authService.getUid();

    this.removeIncomingCall(detailCalling.id);
    this.callingObj.currentCalling = {
      ...(this.isCurrentCalling(detailCalling.id) ? this.callingObj.currentCalling : {}),
      ...detailCalling,
    };
    this.callingObj.isCallingIn = false;
    this.callingObj.isCallingOut = false;
    this.callingObj.isCalling = isHandledByMe
      && [
        CALL_STATUS.ACCEPTED,
        CALL_STATUS.MEDIA_UPDATE,
        CALL_STATUS.IN_CALLING,
      ].includes(detailCalling.status as CALL_STATUS);
    this.touchState();
  }

  getIncomingDisplayList(): IBusinessCallingItem[] {
    const items: IBusinessCallingItem[] = [];

    this.callingObj.incomingCallList.forEach(call => {
      if (call?.id && !items.some(item => item.id == call.id)) {
        items.push(call);
      }
    });

    return items;
  }

  isConversationInActiveCall(pageId: string | number, pageUserId: string | number): boolean {
    const currentCalling = this.callingObj.currentCalling;
    if (!currentCalling?.id || !pageId || !pageUserId) {
      return false;
    }

    const isSameConversation = String(currentCalling.pageId) === String(pageId)
      && String(currentCalling.pageUserId) === String(pageUserId);

    if (!isSameConversation) {
      return false;
    }

    return this.callingObj.isCalling
      || this.callingObj.isCallingOut
      || [
        CALL_STATUS.ACCEPTED,
        CALL_STATUS.MEDIA_UPDATE,
        CALL_STATUS.IN_CALLING,
      ].includes(currentCalling.status as CALL_STATUS);
  }

  addIncomingCall(call: IBusinessCallingItem): void {
    if (!call?.id || this.callingObj.incomingCallList.some(item => item.id == call.id)) {
      return;
    }

    this.callingObj.incomingCallList.push(call);
    this.touchState();
  }

  removeIncomingCall(callId: string | number): void {
    this.callingObj.incomingCallList = this.callingObj.incomingCallList.filter(item => item.id !== callId);
    this.touchState();
  }

  markAcceptingCall(call: IBusinessCallingItem): {
    isCurrentCalling: boolean;
    previousCurrentCalling: IBusinessCallingItem | null;
  } {
    const isCurrentCalling = call?.id == this.callingObj.currentCalling?.id;
    const previousCurrentCalling = this.callingObj.currentCalling;

    if (isCurrentCalling && this.callingObj.currentCalling) {
      this.callingObj.currentCalling.isConnecting = true;
    }
    call.isConnecting = true;
    this.touchState();

    return {isCurrentCalling, previousCurrentCalling};
  }

  setAcceptedIncomingCall(
    call: IBusinessCallingItem,
    user: { id: string | number; name: string },
    previousCurrentCalling: IBusinessCallingItem | null,
    isCurrentCalling: boolean,
  ): void {
    const callId = call?.id;
    this.callingObj.isCalling = true;
    this.callingObj.isCallingIn = false;
    this.callingObj.isCallingOut = false;

    if (!isCurrentCalling
      && previousCurrentCalling?.id
      && previousCurrentCalling.id != callId
      && previousCurrentCalling.status === CALL_STATUS.CONNECTED
      && previousCurrentCalling.direction === CALLING_DIRECTION.INCOMING) {
      this.addIncomingCall(previousCurrentCalling);
    }

    this.callingObj.currentCalling = {
      ...call,
      status: CALL_STATUS.IN_CALLING,
      handledById: user.id,
      handledByName: user.name,
    };

    if (callId) {
      this.removeIncomingCall(callId);
    }
    delete call.isConnecting;
    delete this.callingObj.currentCalling.isConnecting;
    this.touchState();
  }

  setOutgoingCalling(callingData: IBusinessCallingItem): void {
    if (this.callingObj.currentCalling) {
      this.minimizeCalling(this.callingObj.currentCalling);
    }

    this.callingObj.currentCalling = callingData;
    this.callingObj.isCallingOut = true;
    this.touchState();
  }

  minimizeCalling(call: IBusinessCallingItem): void {
    this.callingObj.isCallingIn = false;
    this.addIncomingCall(call);
    this.callingObj.currentCalling = null;
    this.touchState();
  }

  canStartCallingInConversation(pageId: string | number | null | undefined, pageUserId: string | number | null | undefined): boolean {
    if (!pageId || !pageUserId) {
      this.notify.warning('Không thể gọi. Thiếu thông tin trang hoặc khách hàng.');
      return false;
    }

    if (!this.canAcceptCall(this.authService.getUid())) {
      this.notify.warning('Bạn đang trong một cuộc gọi khác. Vui lòng kết thúc cuộc gọi hiện tại trước.');
      return false;
    }

    if (this.isConversationInActiveCall(pageId, pageUserId)) {
      const handledByName = this.callingObj.currentCalling?.handledByName || 'nhân viên khác';
      this.notify.warning(`Hội thoại này đang trong cuộc gọi với ${handledByName}.`);
      return false;
    }

    return true;
  }

  isFacebookInbox(conversation: IConversation | null | undefined): boolean {
    return conversation?.channel === CHANNEL.FACEBOOK && conversation?.type === CONVERSATION_TYPE.INBOX;
  }

  async acceptCall(call: IBusinessCallingItem | null | undefined): Promise<void> {
    const pageId = call?.pageId;
    const callId = call?.id;
    if (!call || !callId || !pageId) return;

    if (!this.canStartCallingInConversation(pageId, call.pageUserId)) {
      return;
    }

    if (this.isWhatsappIncomingCall(call)) {
      await this.acceptWhatsappIncomingCall(call);
      return;
    }

    const peerConnection = this.createPeerConnection();
    this.peerConnection = peerConnection;
    const session = await this.createCallSession(peerConnection);
    if (!session) return;

    const {isCurrentCalling, previousCurrentCalling} = this.markAcceptingCall(call);
    this.businessCallingApiService.acceptCalling({
      pageId: String(pageId),
      callId: String(callId),
      action: 'accept',
      session: JSON.stringify(session),
      channel: String(call.pageId).includes('wa') ? CHANNEL.WHATSAPP : CHANNEL.FACEBOOK,
    }).subscribe({
      next: async res => {
        if (res?.code !== 1) {
          await this.hangUp();
          delete call.isConnecting;
          if (isCurrentCalling && this.callingObj.currentCalling?.isConnecting) {
            delete this.callingObj.currentCalling.isConnecting;
          }
          this.touchState();
          return;
        }

        const pc = this.peerConnection;
        const sessionData = (res as any)?.data?.session;
        if (!pc || !sessionData?.sdp_response?.sdp) return;

        await pc.setRemoteDescription(new RTCSessionDescription({
          type: 'answer',
          sdp: sessionData.sdp_response.sdp,
        }));

        if (sessionData.sdp_renegotiation?.sdp) {
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'offer',
            sdp: sessionData.sdp_renegotiation.sdp,
          }));

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
        }

        this.setAcceptedIncomingCall(call, {
          id: this.authService.getUid(),
          name: 'Bạn',
        }, previousCurrentCalling, isCurrentCalling);

        this.realtime.emitCalling(pageId, {
          calling: this.callingObj.currentCalling,
        });
        this.startTimer();
      },
      error: async err => {
        console.log(err);
        await this.hangUp();
        delete call.isConnecting;
        if (isCurrentCalling && this.callingObj.currentCalling?.isConnecting) {
          delete this.callingObj.currentCalling.isConnecting;
        }
        this.touchState();
      },
    });
  }

  private async acceptWhatsappIncomingCall(call: IBusinessCallingItem): Promise<void> {
    const pageId = call.pageId;
    const callId = call.id;
    const remoteOffer = this.getWhatsappRemoteOffer(call);
    if (!pageId || !callId || !remoteOffer?.sdp) {
      this.notify.warning('Không thể nhận cuộc gọi WhatsApp. Thiếu thông tin SDP.');
      return;
    }

    const peerConnection = this.createPeerConnection();
    this.peerConnection = peerConnection;
    this.handledRemoteSdpKeys.clear();
    const session = await this.createAnswerSession(peerConnection, remoteOffer);
    if (!session) return;
    this.handledRemoteSdpKeys.add(this.getRemoteSdpKey(callId, remoteOffer));

    const {isCurrentCalling, previousCurrentCalling} = this.markAcceptingCall(call);
    const actionParams = {
      pageId: String(pageId),
      callId: String(callId),
      session: JSON.stringify(session),
      channel: CHANNEL.WHATSAPP,
      phoneNumberId: call.phoneNumberId ? String(call.phoneNumberId) : undefined,
    };

    this.businessCallingApiService.preAcceptCalling({
      ...actionParams,
      action: 'pre_accept',
    }).subscribe({
      next: preAcceptRes => {
        if (!this.isCallingApiSuccess(preAcceptRes)) {
          void this.clearAcceptingCallAfterFailure(call, isCurrentCalling);
          return;
        }

        this.businessCallingApiService.acceptCalling({
          ...actionParams,
          action: 'accept',
        }).subscribe({
          next: acceptRes => {
            if (!this.isCallingApiSuccess(acceptRes)) {
              void this.clearAcceptingCallAfterFailure(call, isCurrentCalling);
              return;
            }

            this.setAcceptedIncomingCall(call, {
              id: this.authService.getUid(),
              name: 'Bạn',
            }, previousCurrentCalling, isCurrentCalling);
            this.realtime.emitCalling(pageId, {
              calling: this.callingObj.currentCalling,
            });
            this.startTimer();
          },
          error: err => {
            console.log(err);
            void this.clearAcceptingCallAfterFailure(call, isCurrentCalling);
          },
        });
      },
      error: err => {
        console.log(err);
        void this.clearAcceptingCallAfterFailure(call, isCurrentCalling);
      },
    });
  }

  async callToCustomer(conversation: IConversation | null | undefined): Promise<void> {
    if (!conversation || !this.isFacebookInbox(conversation)) return;

    await this.callToPageUser(
      conversation.fanpageId,
      conversation.fanpageUserId,
      conversation.fanpageUserName,
      conversation.customerAvatar,
    );
  }

  async callToPageUser(
    pageId: string | number | null | undefined,
    pageUserId: string | number | null | undefined,
    pageUserName = '',
    customerAvatar = '',
  ): Promise<void> {
    if (!this.canStartCallingInConversation(pageId, pageUserId)) {
      return;
    }

    const peerConnection = this.createPeerConnection();
    this.peerConnection = peerConnection;
    this.handledRemoteSdpKeys.clear();
    const session = await this.createCallSession(peerConnection);
    if (!session) return;

    this.businessCallingApiService.callToCustomer({
      pageId: String(pageId),
      recipientId: String(pageUserId),
      session: JSON.stringify(session),
    }).subscribe({
      next: res => {
        if (res?.code !== 1) {
          this.notify.warning((res as any)?.messages || 'Không thể bắt đầu cuộc gọi.');
          return;
        }

        const callingData = (res as any)?.data || {};
        this.setOutgoingCalling({
          ...callingData,
          id: callingData?.id || callingData?.call_id || '',
          pageId,
          pageUserId,
          pageUserName,
          customerAvatar,
          handledById: this.authService.getUid(),
        });
      },
      error: err => {
        console.log(err);
        this.notify.warning('Không thể bắt đầu cuộc gọi.');
      },
    });
  }

  requestRecall(pageId: string | number | null | undefined, pageUserId: string | number | null | undefined): void {
    if (!pageId || !pageUserId) return;

    this.businessCallingApiService.requestRecall({
      pageId: String(pageId),
      recipientId: String(pageUserId),
    }).subscribe({
      next: res => {
        const message = res.code === 1
          ? (res as any)?.data?.messages
          : ((res as any)?.message || 'Yêu cầu gọi lại thất bại');
        if (res.code === 1) {
          this.notify.success(message);
        } else {
          this.notify.warning(message);
        }
      },
      error: () => this.notify.warning('Yêu cầu gọi lại thất bại'),
    });
  }

  syncRingingHandledBy(call: IBusinessCallingItem): void {
    const handledById = this.callingObj.currentCalling?.handledById;
    if (!call?.pageId || !call?.id || !handledById) return;

    this.businessCallingApiService.updateHandledBy({
      pageId: String(call.pageId),
      callId: String(call.id),
      handledById: String(handledById),
    }).subscribe({
      error: err => console.log('Lỗi cập nhật handledBy', err?.message),
    });
  }

  async handleRealtimeCallingData(data: unknown): Promise<void> {
    const callingData = this.extractRealtimeCallingData(data);
    if (!callingData?.id) return;
    this.normalizeCallingData(callingData);

    if (callingData.status !== CALL_STATUS.CONNECTED) {
      this.removeIncomingCall(callingData.id);
    }

    switch (callingData.status) {
      case CALL_STATUS.CONNECTED:
        if (callingData.direction === CALLING_DIRECTION.INCOMING && !this.isCurrentCalling(callingData.id)) {
          this.setIncomingCalling(callingData);
        }
        if (this.isWhatsappIncomingCall(callingData) && this.peerConnection) {
          await this.syncCurrentCallingSession(callingData, true);
        }
        break;

      case CALL_STATUS.COMPLETED: {
        this.removeIncomingCall(callingData.id);
        const isCurrentCallingCompleted = callingData.id == this.callingObj.currentCalling?.id;
        if (isCurrentCallingCompleted) {
          this.callingObj.isCallingIn = false;
          this.callingObj.currentCalling = null;
          await this.hangUp();
        }
        this.endCalling(callingData, (callingData as any).duration > 0 ? 'terminate' : 'reject');
        this.touchState();
        break;
      }

      case CALL_STATUS.RINGING:
        this.syncRingingHandledBy(callingData);
        break;

      case CALL_STATUS.ACCEPTED: {
        const handledById = callingData.handledById || '';
        if (handledById != this.authService.getUid() && !this.isCurrentCalling(callingData.id)) {
          return;
        }

        this.syncHandledCalling(callingData, this.authService.getUid());
        break;
      }

      case CALL_STATUS.MEDIA_UPDATE: {
        await this.syncCurrentCallingSession(callingData);
        break;
      }

      case CALL_STATUS.IN_CALLING:
        this.syncHandledCalling(callingData, this.authService.getUid());
        break;
    }
  }

  createPeerConnection(): RTCPeerConnection {
    const pc = new RTCPeerConnection();

    pc.ontrack = event => {
      const stream = event.streams?.[0] || new MediaStream([event.track]);
      if (!stream) {
        return;
      }

      if (!this.remoteAudioEl) {
        this.remoteAudioEl = this.document.createElement('audio');
        this.remoteAudioEl.autoplay = true;
        this.remoteAudioEl.style.display = 'none';
        this.document.body.appendChild(this.remoteAudioEl);
      }

      if (this.remoteAudioEl.srcObject !== stream) {
        this.remoteAudioEl.srcObject = stream;
      }

      this.remoteAudioEl.play().catch(err => {
        this.markAudioPlaybackBlocked();
        console.log('Không phát được remote audio', err);
      });
    };

    pc.onconnectionstatechange = () => {
      console.log('Calling peer connection state:', pc.connectionState);
    };

    pc.oniceconnectionstatechange = () => {
      console.log('Calling ICE connection state:', pc.iceConnectionState);
    };

    return pc;
  }

  async refreshMediaPermissions(deferState = false): Promise<void> {
    const microphoneState = await this.getMicrophonePermissionState();
    this.updateMediaPermission({microphoneState}, deferState);
    await this.refreshAudioDevices(undefined, deferState);
  }

  shouldShowMicrophonePermissionButton(): boolean {
    return this.mediaPermission.microphoneState === 'prompt'
      || this.mediaPermission.microphoneState === 'denied';
  }

  async refreshAudioDevices(activeStream?: MediaStream, deferState = false): Promise<void> {
    if (!this.isBrowser || !navigator.mediaDevices?.enumerateDevices) {
      this.updateMediaPermission({
        audioInputLabel: '',
        audioOutputLabel: '',
      }, deferState);
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
      const activeInputLabel = activeStream?.getAudioTracks()?.[0]?.label || '';
      const defaultInput = audioInputs.find(device => device.deviceId === 'default') || audioInputs[0];
      const defaultOutput = audioOutputs.find(device => device.deviceId === 'default') || audioOutputs[0];

      this.updateMediaPermission({
        audioInputLabel: this.normalizeDeviceLabel(
          activeInputLabel || defaultInput?.label || '',
          audioInputs.length ? 'Micro mặc định' : '',
        ),
        audioOutputLabel: this.normalizeDeviceLabel(
          defaultOutput?.label || '',
          audioOutputs.length ? 'Loa mặc định' : '',
        ),
      }, deferState);
    } catch {
      this.updateMediaPermission({
        audioInputLabel: '',
        audioOutputLabel: '',
      }, deferState);
    }
  }

  getAudioDeviceDisplayText(): string {
    const parts: string[] = [];

    if (this.mediaPermission.audioInputLabel) {
      parts.push(`<i class="ph ph-microphone me-1 call-popup-icon-sound"></i> ${this.mediaPermission.audioInputLabel}`);
    }

    if (this.mediaPermission.audioOutputLabel) {
      parts.push(`<br><i class="ph ph-speaker-high me-1 mt-2 call-popup-icon-sound"></i> ${this.mediaPermission.audioOutputLabel}`);
    }

    return parts.join('\n');
  }

  shouldShowAudioPlaybackButton(): boolean {
    return !this.mediaPermission.isAudioPlaybackUnlocked;
  }

  async requestMicrophonePermission(): Promise<boolean> {
    if (this.mediaPermission.isRequestingMicrophone) {
      return false;
    }

    this.updateMediaPermission({isRequestingMicrophone: true});
    try {
      const microphonePermission = await this.getMicrophonePermissionState();
      this.updateMediaPermission({microphoneState: microphonePermission}, true);

      if (microphonePermission === 'denied') {
        this.notify.warning('Micro đang bị chặn. Vui lòng bấm biểu tượng ổ khóa trên thanh địa chỉ và cho phép Micro.');
        return false;
      }

      if (!this.hasGetUserMediaSupport()) {
        this.notify.warning('Trình duyệt không hỗ trợ quyền Micro. Vui lòng kiểm tra lại trình duyệt hoặc kết nối HTTPS.');
        return false;
      }

      this.notify.info('Vui lòng chọn Cho phép để cấp quyền Micro.');
      const stream = await this.getAudioStream();
      await this.refreshAudioDevices(stream, true);
      stream.getTracks().forEach(track => track.stop());

      this.updateMediaPermission({
        microphoneState: await this.getMicrophonePermissionState() || 'granted',
      }, true);
      this.notify.success('Đã cấp quyền Micro.');
      return true;
    } catch (error: unknown) {
      await this.refreshMediaPermissions(true);
      const err = error as Error;
      const errorMsg = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
        ? 'Bạn chưa cấp quyền Micro. Vui lòng cho phép Micro để nhận cuộc gọi.'
        : `Không thể xin quyền Micro: ${err?.message || 'Lỗi không xác định'}`;
      this.notify.warning(errorMsg);
      return false;
    } finally {
      this.updateMediaPermission({isRequestingMicrophone: false}, true);
    }
  }

  async unlockAudioPlayback(): Promise<boolean> {
    if (this.mediaPermission.isUnlockingAudio) {
      return false;
    }

    this.updateMediaPermission({isUnlockingAudio: true});
    try {
      const audioContextClass = (window as Window & {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      }).AudioContext || (window as Window & {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      }).webkitAudioContext;

      if (audioContextClass) {
        const audioContext = new audioContextClass();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        gain.gain.value = 0;
        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.01);
        setTimeout(() => void audioContext.close?.(), 100);
      } else {
        const audio = new Audio();
        audio.muted = true;
        await audio.play();
        audio.pause();
      }

      this.markAudioPlaybackUnlocked(true);
      this.notify.success('Đã bật âm thanh cuộc gọi.');
      return true;
    } catch {
      this.notify.warning('Trình duyệt chưa cho phép phát âm thanh. Vui lòng kiểm tra quyền Sound của trang và bấm lại nút Bật loa.');
      return false;
    } finally {
      this.updateMediaPermission({isUnlockingAudio: false}, true);
    }
  }

  async createCallSession(pc: RTCPeerConnection): Promise<{ sdp: string | undefined; sdp_type: 'offer' } | null> {
    let stream: MediaStream | null = null;
    try {
      if (!this.hasGetUserMediaSupport()) {
        this.notify.warning('Trình duyệt không hỗ trợ quyền Micro. Vui lòng kiểm tra lại trình duyệt hoặc kết nối HTTPS.');
        this.closePeerConnection(pc);
        return null;
      }

      const microphonePermission = await this.getMicrophonePermissionState();
      if (microphonePermission === 'denied') {
        this.notify.warning('Micro đang bị chặn. Vui lòng mở quyền Micro trong cài đặt trình duyệt rồi thử lại.');
        this.closePeerConnection(pc);
        return null;
      }

      if (microphonePermission === 'prompt') {
        this.notify.info('Vui lòng chọn Cho phép để cấp quyền Micro và nhận cuộc gọi.');
      }

      stream = await this.getAudioStream();

      stream.getTracks().forEach(track => pc.addTrack(track, stream as MediaStream));
      this.updateMediaPermission({
        microphoneState: await this.getMicrophonePermissionState() || 'granted',
      }, true);
      await this.refreshAudioDevices(stream, true);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      return {sdp: offer.sdp, sdp_type: 'offer'};
    } catch (error: unknown) {
      const err = error as Error;
      let errorMsg = '';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMsg = 'Bạn cần cấp quyền Micro để thực hiện cuộc gọi. Nếu đã chặn trước đó, hãy mở quyền Micro trong cài đặt trình duyệt.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMsg = 'Không tìm thấy thiết bị Micro trên máy tính.';
      } else {
        errorMsg = `Đã xảy ra lỗi khi kết nối Micro: ${err.message}`;
      }

      stream?.getTracks().forEach(track => track.stop());
      this.closePeerConnection(pc);
      this.notify.warning(errorMsg);
      return null;
    }
  }

  private async createAnswerSession(
    pc: RTCPeerConnection,
    remoteOffer: { sdp: string; sdp_type?: RTCSdpType },
  ): Promise<{ sdp: string | undefined; sdp_type: 'answer' } | null> {
    let stream: MediaStream | null = null;
    try {
      if (!this.hasGetUserMediaSupport()) {
        this.notify.warning('Trình duyệt không hỗ trợ quyền Micro. Vui lòng kiểm tra lại trình duyệt hoặc kết nối HTTPS.');
        this.closePeerConnection(pc);
        return null;
      }

      const microphonePermission = await this.getMicrophonePermissionState();
      if (microphonePermission === 'denied') {
        this.notify.warning('Micro đang bị chặn. Vui lòng mở quyền Micro trong cài đặt trình duyệt rồi thử lại.');
        this.closePeerConnection(pc);
        return null;
      }

      if (microphonePermission === 'prompt') {
        this.notify.info('Vui lòng chọn Cho phép để cấp quyền Micro và nhận cuộc gọi.');
      }

      await pc.setRemoteDescription(new RTCSessionDescription({
        type: remoteOffer.sdp_type || 'offer',
        sdp: remoteOffer.sdp,
      }));

      stream = await this.getAudioStream();
      stream.getTracks().forEach(track => pc.addTrack(track, stream as MediaStream));
      this.updateMediaPermission({
        microphoneState: await this.getMicrophonePermissionState() || 'granted',
      }, true);
      await this.refreshAudioDevices(stream, true);

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      return {sdp: answer.sdp, sdp_type: 'answer'};
    } catch (error: unknown) {
      const err = error as Error;
      stream?.getTracks().forEach(track => track.stop());
      this.closePeerConnection(pc);
      this.notify.warning(`Không thể thiết lập âm thanh cuộc gọi WhatsApp: ${err?.message || 'Lỗi không xác định'}`);
      return null;
    }
  }

  async hangUp(): Promise<void> {
    this.stopTimer();
    try {
      if (this.peerConnection) {
        this.peerConnection.getSenders().forEach(sender => {
          sender.track?.stop();
        });

        this.peerConnection.close();
        this.peerConnection = null;
      }

      if (this.remoteAudioEl) {
        this.remoteAudioEl.pause();
        this.remoteAudioEl.srcObject = null;
        this.remoteAudioEl.remove();
        this.remoteAudioEl = undefined;
      }

      this.callingObj.isCalling = false;
      this.callingObj.isCallingIn = false;
      this.callingObj.isCallingOut = false;
      this.touchState();
    } catch (error) {
      console.error('Lỗi khi gác máy:', error);
    }
  }

  toggleMic(): void {
    if (!this.peerConnection) {
      console.warn('Không có cuộc gọi nào đang diễn ra.');
      return;
    }

    const audioSender = this.peerConnection
      .getSenders()
      .find(sender => sender.track?.kind === 'audio');

    if (audioSender?.track) {
      this.isMicEnabled = !this.isMicEnabled;
      audioSender.track.enabled = this.isMicEnabled;
      return;
    }

    this.notify.warning('Không tìm thấy thiết bị Micro để bật/tắt.');
  }

  private async syncCurrentCallingSession(
    callingData: IBusinessCallingItem,
    shouldHandleRootSessionSdp = false,
  ): Promise<void> {
    const currentCallId = this.callingObj.currentCalling?.id || '';
    if (!currentCallId || callingData.id != currentCallId) {
      return;
    }

    const previousCalling = this.callingObj.currentCalling;
    const currentSession = previousCalling?.session;
    const mergedSession = {
      ...(currentSession || {}),
      ...(callingData.session || {}),
    };
    const activeStatuses = [
      CALL_STATUS.ACCEPTED,
      CALL_STATUS.MEDIA_UPDATE,
      CALL_STATUS.IN_CALLING,
    ];
    const nextStatus = callingData.status === CALL_STATUS.CONNECTED
      && activeStatuses.includes(previousCalling?.status as CALL_STATUS)
      ? previousCalling?.status
      : callingData.status;

    this.callingObj.currentCalling = {
      ...(previousCalling || {}),
      ...callingData,
      status: nextStatus,
      session: mergedSession,
    };

    if (mergedSession.sdp_response) {
      await this.handleCallOutAnswer(mergedSession.sdp_response);
    }

    if (mergedSession.sdp_renegotiation) {
      await this.handleCallOutAnswer(mergedSession.sdp_renegotiation);
    }

    if (shouldHandleRootSessionSdp && mergedSession.sdp && mergedSession.sdp_type) {
      await this.handleCallOutAnswer({
        sdp: mergedSession.sdp,
        sdp_type: mergedSession.sdp_type,
      });
    }

    this.touchState();
  }

  async handleCallOutAnswer(sdpResponse: { sdp?: string; sdp_type?: RTCSdpType }): Promise<void> {
    try {
      if (!this.peerConnection) {
        console.error('PeerConnection chưa được khởi tạo.');
        return;
      }

      if (!sdpResponse.sdp || !sdpResponse.sdp_type) {
        return;
      }

      const callId = this.callingObj.currentCalling?.id || '';
      const sdpKey = this.getRemoteSdpKey(callId, sdpResponse);
      if (this.handledRemoteSdpKeys.has(sdpKey)) {
        return;
      }

      if (sdpResponse.sdp_type === 'answer' && this.peerConnection.signalingState !== 'have-local-offer') {
        console.log('Bỏ qua answer vì signalingState hiện tại là:', this.peerConnection.signalingState);
        this.handledRemoteSdpKeys.add(sdpKey);
        return;
      }

      if (sdpResponse.sdp_type === 'offer' && this.peerConnection.signalingState !== 'stable') {
        console.log('Bỏ qua offer vì signalingState hiện tại là:', this.peerConnection.signalingState);
        return;
      }

      await this.peerConnection.setRemoteDescription(new RTCSessionDescription({
        sdp: sdpResponse.sdp,
        type: sdpResponse.sdp_type,
      }));
      this.handledRemoteSdpKeys.add(sdpKey);

      if (sdpResponse.sdp_type === 'answer') {
        this.callingObj.isCalling = true;
        this.callingObj.isCallingOut = false;
        this.startTimer();
        this.touchState();
        return;
      }

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      this.callingObj.isCalling = true;
      this.callingObj.isCallingOut = false;
      this.startTimer();
      this.touchState();
    } catch (error) {
      console.error('Lỗi đàm phán cuộc gọi đi:', error);
      this.notify.error('Lỗi thiết lập âm thanh cuộc gọi.');
      void this.hangUp();
    }
  }

  private getRemoteSdpKey(callId: string | number, sdpResponse: { sdp?: string; sdp_type?: RTCSdpType }): string {
    return [
      callId || 'unknown-call',
      sdpResponse.sdp_type || 'unknown-type',
      sdpResponse.sdp || '',
    ].join('|');
  }

  private isWhatsappIncomingCall(call: IBusinessCallingItem): boolean {
    return String(call.pageId).includes('wa')
      || !!call.phoneNumberId
      || call.session?.sdp_type === 'offer';
  }

  private getWhatsappRemoteOffer(call: IBusinessCallingItem): { sdp: string; sdp_type?: RTCSdpType } | null {
    const session = call.session;
    if (!session?.sdp) {
      return null;
    }

    return {
      sdp: session.sdp,
      sdp_type: session.sdp_type || 'offer',
    };
  }

  private isCallingApiSuccess(res: unknown): boolean {
    const response = res as { code?: number; success?: boolean; data?: { success?: boolean } } | undefined;
    return response?.code === 1 || response?.success === true || response?.data?.success === true;
  }

  private async clearAcceptingCallAfterFailure(call: IBusinessCallingItem, isCurrentCalling: boolean): Promise<void> {
    await this.hangUp();
    delete call.isConnecting;
    if (isCurrentCalling && this.callingObj.currentCalling?.isConnecting) {
      delete this.callingObj.currentCalling.isConnecting;
    }
    this.touchState();
  }

  startTimer(): void {
    this.stopTimer();
    this.callingObj.callTimer.totalCallSeconds = 0;
    this.callingObj.callTimer.displayCallTime = '00:00';

    this.callingObj.callTimer.callTimer = setInterval(() => {
      this.callingObj.callTimer.totalCallSeconds++;

      const minutes = Math.floor(this.callingObj.callTimer.totalCallSeconds / 60);
      const seconds = this.callingObj.callTimer.totalCallSeconds % 60;

      const strMinutes = minutes < 10 ? `0${minutes}` : String(minutes);
      const strSeconds = seconds < 10 ? `0${seconds}` : String(seconds);

      this.callingObj.callTimer.displayCallTime = `${strMinutes}:${strSeconds}`;
      this.touchState();
    }, 1000);
  }

  stopTimer(): void {
    if (this.callingObj.callTimer.callTimer) {
      clearInterval(this.callingObj.callTimer.callTimer);
      this.callingObj.callTimer.callTimer = null;
    }
  }

  endCalling(call: IBusinessCallingItem, action: 'reject' | 'terminate' = 'terminate'): void {
    const pageId = call.pageId;
    const callId = call.id;
    if (!callId || !pageId) {
      return;
    }

    if (this.callingObj.currentCalling?.isEnding || call?.isEnding) {
      return;
    }

    const endCurrentCalling = call.id == this.callingObj.currentCalling?.id;
    if (endCurrentCalling && this.callingObj.currentCalling) {
      this.callingObj.currentCalling.isEnding = true;
    }

    call.isEnding = true;
    this.touchState();
    this.businessCallingApiService.endCalling({
      pageId: String(pageId),
      callId: String(callId),
      action,
      channel: String(pageId).includes('wa') ? CHANNEL.WHATSAPP : CHANNEL.FACEBOOK,
      phoneNumberId: call.phoneNumberId ? String(call.phoneNumberId) : undefined,
    }).subscribe({
      next: () => {
        this.clearEndingState(call, endCurrentCalling);
      },
      error: err => {
        console.log(err);
        this.clearEndingState(call, endCurrentCalling);
      },
    });
  }

  formatCallingDuration(durationSeconds: number): string {
    if (!durationSeconds || durationSeconds <= 0) {
      return '';
    }

    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    const seconds = durationSeconds % 60;

    if (hours > 0) {
      return `${hours} giờ ${minutes} phút ${seconds} giây`;
    }

    if (minutes > 0) {
      return `${minutes} phút ${seconds} giây`;
    }

    return `${seconds} giây`;
  }

  private async getMicrophonePermissionState(): Promise<PermissionState | null> {
    if (!this.isBrowser || !navigator.permissions?.query) {
      return null;
    }

    try {
      const permissionStatus = await navigator.permissions.query({name: 'microphone' as PermissionName});
      return permissionStatus.state;
    } catch {
      return null;
    }
  }

  private hasGetUserMediaSupport(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const legacyNavigator = navigator as NavigatorWithLegacyMedia;
    return !!navigator.mediaDevices?.getUserMedia
      || !!legacyNavigator.getUserMedia
      || !!legacyNavigator.webkitGetUserMedia
      || !!legacyNavigator.mozGetUserMedia;
  }

  private getAudioStream(): Promise<MediaStream> {
    const constraints: MediaStreamConstraints = {audio: true, video: false};

    if (navigator.mediaDevices?.getUserMedia) {
      return navigator.mediaDevices.getUserMedia(constraints);
    }

    const legacyNavigator = navigator as NavigatorWithLegacyMedia;
    const legacyGetUserMedia = legacyNavigator.getUserMedia
      || legacyNavigator.webkitGetUserMedia
      || legacyNavigator.mozGetUserMedia;

    if (!legacyGetUserMedia) {
      return Promise.reject(new Error('getUserMedia is not supported'));
    }

    return new Promise<MediaStream>((resolve, reject) => {
      legacyGetUserMedia.call(legacyNavigator, constraints, resolve, reject);
    });
  }

  private closePeerConnection(pc: RTCPeerConnection): void {
    pc.getSenders().forEach(sender => sender.track?.stop());
    pc.close();

    if (this.peerConnection === pc) {
      this.peerConnection = null;
    }
  }

  private normalizeDeviceLabel(label: string, fallback: string): string {
    if (!label) {
      return fallback;
    }

    return label.replace(/^Default\s*-\s*/i, '').trim() || fallback;
  }

  private extractIncomingCalls(res: unknown): IBusinessCallingItem[] {
    const whatsappCalls = this.extractWhatsappWebhookCalls(res);
    if (whatsappCalls.length) {
      return whatsappCalls;
    }

    const root = res as {
      items?: unknown;
      data?: {
        items?: unknown;
        calling?: unknown;
        incomingCallList?: unknown;
      } | unknown;
    };
    const data = root?.data as {
      items?: unknown;
      calling?: unknown;
      incomingCallList?: unknown;
    } | IBusinessCallingItem[] | undefined;
    const rawItems = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.incomingCallList)
          ? data.incomingCallList
          : Array.isArray(root?.items)
            ? root.items
            : data?.calling
              ? [data.calling]
              : [];

    return rawItems.filter((call): call is IBusinessCallingItem => {
      return !!call && typeof call === 'object' && !!(call as IBusinessCallingItem).id;
    });
  }

  private extractRealtimeCallingData(data: unknown): IBusinessCallingItem | null {
    const whatsappCalls = this.extractWhatsappWebhookCalls(data);
    if (whatsappCalls.length) {
      return whatsappCalls[0];
    }

    const payload = data as ({ calling?: IBusinessCallingItem; data?: unknown } & IBusinessCallingItem) | undefined;
    const nestedPayload = payload?.data as { calling?: IBusinessCallingItem } | undefined;
    return payload?.calling
      || nestedPayload?.calling
      || (payload?.id ? payload as IBusinessCallingItem : null);
  }

  private extractWhatsappWebhookCalls(payload: unknown): IBusinessCallingItem[] {
    const root = payload as { object?: string; entry?: unknown[]; data?: unknown } | undefined;
    if (root?.data && typeof root.data === 'object') {
      const nested = this.extractWhatsappWebhookCalls(root.data);
      if (nested.length) {
        return nested;
      }
    }

    if (root?.object !== 'whatsapp_business_account' || !Array.isArray(root.entry)) {
      return [];
    }

    const result: IBusinessCallingItem[] = [];
    root.entry.forEach(entryItem => {
      const entry = entryItem as { id?: string | number; changes?: unknown[] };
      if (!Array.isArray(entry.changes)) {
        return;
      }

      entry.changes.forEach(changeItem => {
        const change = changeItem as { field?: string; value?: any };
        const value = change.value;
        if (change.field !== 'calls' || !Array.isArray(value?.calls)) {
          return;
        }

        const metadata = value.metadata || {};
        const contact = Array.isArray(value.contacts) ? value.contacts[0] : null;
        value.calls.forEach((rawCall: any) => {
          if (!rawCall?.id) {
            return;
          }

          result.push({
            ...rawCall,
            id: rawCall.id,
            channel: CHANNEL.WHATSAPP,
            pageId: metadata.phone_number_id || rawCall.phone_number_id || entry.id,
            phoneNumberId: metadata.phone_number_id || rawCall.phone_number_id,
            displayPhoneNumber: metadata.display_phone_number,
            pageUserId: contact?.wa_id || rawCall.from_user_id || rawCall.from,
            pageUserName: contact?.profile?.name || contact?.profile?.username || rawCall.from || 'WhatsApp User',
            status: rawCall.event,
            direction: rawCall.direction || 'USER_INITIATED',
            createdAt: Number(rawCall.timestamp || 0),
            duration: Number(rawCall.duration || 0),
            session: rawCall.session,
          });
        });
      });
    });

    return result;
  }

  private updateMediaPermission(
    patch: Partial<IBusinessCallingMediaPermission>,
    deferState = false,
  ): void {
    const applyPatch = () => {
      Object.assign(this.mediaPermission, patch);
      this.touchState();
    };

    if (deferState) {
      setTimeout(applyPatch);
      return;
    }

    applyPatch();
  }

  private markAudioPlaybackUnlocked(deferState = false): void {
    this.updateMediaPermission({isAudioPlaybackUnlocked: true}, deferState);
  }

  private markAudioPlaybackBlocked(): void {
    this.updateMediaPermission({isAudioPlaybackUnlocked: false}, true);
  }

  private clearEndingState(call: IBusinessCallingItem, endCurrentCalling: boolean): void {
    delete call.isEnding;

    if (endCurrentCalling && this.callingObj.currentCalling?.isEnding) {
      delete this.callingObj.currentCalling.isEnding;
    }
    this.touchState();
  }

  private normalizeCallingData(call: IBusinessCallingItem): void {
    const rawStatus = Array.isArray(call.status) ? call.status[0] : call.status;
    const statusKey = rawStatus == null ? '' : String(rawStatus).toLowerCase();
    const directionKey = call.direction == null ? '' : String(call.direction).toLowerCase();
    call.status = CALL_STATUS_MAP[statusKey] || call.status;
    call.direction = CALLING_DIRECTION_MAP[directionKey] || call.direction;
    const duration = Number(call.duration ?? 0);

    if (duration > 0) {
      call._label = `${call.direction === CALLING_DIRECTION.INCOMING ? 'Cuộc gọi đến' : 'Cuộc gọi đi'} ${this.formatCallingDuration(duration)}`;
      call._isMissed = false;
      return;
    }

    call._label = call.direction === CALLING_DIRECTION.INCOMING ? 'Cuộc gọi nhỡ' : 'Người nhận bận';
    call._isMissed = true;
  }

  private touchState(): void {
    if (this.stateTouchQueued) {
      return;
    }

    this.stateTouchQueued = true;
    queueMicrotask(() => {
      this.stateTouchQueued = false;
      this._stateVersion.update(value => value + 1);
    });
  }
}
